(() => {
  if (window.GSJComparisonStaggeredBooted) return;
  window.GSJComparisonStaggeredBooted = true;

  const instances = new Map();

  class GSJComparisonStaggered {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.cards = Array.from(root.querySelectorAll('[data-gsj-card]'));
      this.grid = root.querySelector('[data-gsj-card-grid], [data-gsj-grid]');
      this.competitorCards = this.cards.filter((card) => card.dataset.golden !== 'true');
      this.goldenCard = this.cards.find((card) => card.dataset.golden === 'true') || null;
      this.footer = root.querySelector('[data-gsj-footer]');
      this.video = root.querySelector('.gsj-comparison__background-video');
      this.desktopVideoSources = root.querySelector('[data-gsj-desktop-video-sources]');
      this.mobileVideoSources = root.querySelector('[data-gsj-mobile-video-sources]');
      this.animate = root.dataset.animationEnabled === 'true';
      this.replay = root.dataset.replay === 'true';
      this.pauseVideoOffscreen = root.dataset.pauseVideo === 'true';
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.mobileVideoQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.observers = [];
      this.timers = [];
      this.played = false;
      this.videoNear = false;
      this.videoVisible = false;
      this.videoCanPlay = false;
      this.playPending = false;
      this.videoSource = '';
      this.destroyed = false;
      this.horizontalEnabled = false;
      this.horizontalFrame = 0;
      this.horizontalLastX = null;
      this.goldenStagePlayed = false;
      this.sectionHost = null;
      this.overlapSource = null;

      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.onHorizontalScroll = this.onHorizontalScroll.bind(this);
      this.onHorizontalResize = this.onHorizontalResize.bind(this);
      this.updateHeroOverlap = this.updateHeroOverlap.bind(this);
      this.renderHorizontal = this.renderHorizontal.bind(this);
      this.onVideoBreakpointChange = this.onVideoBreakpointChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onVideoPlaying = this.onVideoPlaying.bind(this);
      this.onVideoPause = this.onVideoPause.bind(this);
      this.onVideoCanPlay = this.onVideoCanPlay.bind(this);
      this.onVideoError = this.onVideoError.bind(this);
      this.onUserPlaybackRetry = this.onUserPlaybackRetry.bind(this);

      this.init();
    }

    init() {
      this.root.classList.add('gsj-comparison--js-ready');
      this.setupHeroOverlap();
      this.syncHorizontalMode();
      this.setTimings();

      if (!this.animate || this.reducedMotionQuery.matches || !('IntersectionObserver' in window)) {
        this.revealAll(true);
      } else {
        this.createRevealObservers();
      }

      this.initVideo();
      this.mobileQuery.addEventListener('change', this.onLayoutChange);
      this.reducedMotionQuery.addEventListener('change', this.onLayoutChange);
    }

    syncHorizontalMode() {
      const shouldEnable = !this.reducedMotionQuery.matches
        && !this.mobileQuery.matches
        && Boolean(this.grid)
        && this.competitorCards.length >= 2
        && Boolean(this.goldenCard);

      if (shouldEnable && !this.horizontalEnabled) this.setupHorizontal();
      if (!shouldEnable && this.horizontalEnabled) this.teardownHorizontal();
    }

    setupHeroOverlap() {
      if (this.reducedMotionQuery.matches || this.overlapSource) return;

      const host = this.root.closest('.shopify-section') || this.root.parentElement;
      if (!host) return;

      let previous = host.previousElementSibling;
      while (previous && !previous.matches('.shopify-section')) {
        previous = previous.previousElementSibling;
      }

      if (!previous) return;

      this.sectionHost = host;
      this.overlapSource = previous;
      host.classList.add('gsj-overlap-comparison-host');
      previous.classList.add('gsj-overlap-hero-source');
      this.updateHeroOverlap();
    }

    updateHeroOverlap() {
      if (!this.overlapSource || !this.sectionHost) return;

      const rect = this.root.getBoundingClientRect();
      const active = rect.bottom > 0;
      this.overlapSource.classList.toggle('is-gsj-overlap-active', active);
      this.sectionHost.classList.toggle('is-gsj-overlap-active', active);
    }

    teardownHeroOverlap() {
      if (this.overlapSource) {
        this.overlapSource.classList.remove('gsj-overlap-hero-source', 'is-gsj-overlap-active');
      }
      if (this.sectionHost) {
        this.sectionHost.classList.remove('gsj-overlap-comparison-host', 'is-gsj-overlap-active');
      }
      this.overlapSource = null;
      this.sectionHost = null;
    }

    setupHorizontal() {
      if (this.horizontalEnabled || !this.grid || !this.goldenCard) return;

      this.originalGridParent = this.grid.parentNode;
      this.originalGridNextSibling = this.grid.nextSibling;

      const viewport = document.createElement('div');
      viewport.className = 'gsj-comparison__horizontal-viewport';
      viewport.dataset.gsjHorizontalViewport = '';

      const track = document.createElement('div');
      track.className = 'gsj-comparison__horizontal-track';
      track.dataset.gsjHorizontalTrack = '';

      const competitorStage = document.createElement('div');
      competitorStage.className = 'gsj-comparison__horizontal-stage gsj-comparison__horizontal-stage--competitors';

      const competitorInner = document.createElement('div');
      competitorInner.className = 'gsj-comparison__horizontal-stage-inner';

      const goldenStage = document.createElement('div');
      goldenStage.className = 'gsj-comparison__horizontal-stage gsj-comparison__horizontal-stage--golden';

      const goldenInner = document.createElement('div');
      goldenInner.className = 'gsj-comparison__horizontal-stage-inner gsj-comparison__horizontal-golden-inner';

      this.grid.before(viewport);
      competitorInner.appendChild(this.grid);
      competitorStage.appendChild(competitorInner);
      goldenInner.appendChild(this.goldenCard);
      goldenStage.appendChild(goldenInner);
      track.append(competitorStage, goldenStage);
      viewport.appendChild(track);

      this.horizontalViewport = viewport;
      this.horizontalTrack = track;
      this.competitorStage = competitorStage;
      this.goldenStage = goldenStage;
      this.horizontalEnabled = true;
      this.root.classList.add('gsj-comparison--horizontal');

      window.addEventListener('scroll', this.onHorizontalScroll, { passive: true });
      document.addEventListener('scroll', this.onHorizontalScroll, { passive: true });
      window.addEventListener('resize', this.onHorizontalResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.horizontalResizeObserver = new ResizeObserver(() => {
          this.measureHorizontal();
          this.requestHorizontalRender();
        });
        this.horizontalResizeObserver.observe(viewport);
      }

      const self = this;
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (self.horizontalEnabled) {
            self.measureHorizontal();
            self.updateHeroOverlap();
            self.requestHorizontalRender();
          }
        }, 100);
      });
    }

    teardownHorizontal() {
      if (!this.horizontalEnabled) return;

      window.removeEventListener('scroll', this.onHorizontalScroll);
      document.removeEventListener('scroll', this.onHorizontalScroll);
      window.removeEventListener('resize', this.onHorizontalResize);
      this.horizontalResizeObserver?.disconnect();
      this.horizontalResizeObserver = null;

      if (this.horizontalFrame) cancelAnimationFrame(this.horizontalFrame);
      this.horizontalFrame = 0;

      if (this.grid && this.goldenCard) this.grid.appendChild(this.goldenCard);
      if (this.originalGridParent && this.grid) {
        this.originalGridParent.insertBefore(this.grid, this.originalGridNextSibling);
      }

      this.horizontalViewport?.remove();
      this.horizontalViewport = null;
      this.horizontalTrack = null;
      this.competitorStage = null;
      this.goldenStage = null;
      this.horizontalEnabled = false;
      this.horizontalLastX = null;
      this.goldenStagePlayed = false;
      this.root.classList.remove(
        'gsj-comparison--horizontal',
        'is-horizontal-transitioning',
        'is-golden-stage'
      );
    }

    measureHorizontal() {
      if (!this.horizontalEnabled || !this.horizontalViewport) return;
      this.horizontalViewportWidth = this.horizontalViewport.clientWidth || window.innerWidth;
      this.horizontalScrollDistance = Math.max(1, this.root.offsetHeight - (document.documentElement.clientHeight || window.innerHeight));
    }

    getHorizontalSectionProgress() {
      if (!this.horizontalEnabled) return 0;
      const rect = this.root.getBoundingClientRect();
      return Math.min(1, Math.max(0, -rect.top / this.horizontalScrollDistance));
    }

    requestHorizontalRender() {
      if (!this.horizontalEnabled || this.horizontalFrame) return;
      this.horizontalFrame = requestAnimationFrame(this.renderHorizontal);
    }

    onHorizontalScroll() {
      this.updateHeroOverlap();
      this.requestHorizontalRender();
    }

    onHorizontalResize() {
      this.measureHorizontal();
      this.requestHorizontalRender();
    }

    easeHorizontal(value) {
      return value < 0.5
        ? 4 * value * value * value
        : 1 - Math.pow(-2 * value + 2, 3) / 2;
    }

    revealHorizontalStart(force = false) {
      if (this.played && !this.replay && !force) return;
      this.root.classList.add('is-header-visible');
      this.competitorCards.forEach((card) => card.classList.add('is-visible'));
      this.played = true;
    }

    revealGoldenStage(immediate = false) {
      if (!this.goldenCard || this.goldenStagePlayed) return;
      this.goldenStagePlayed = true;

      const gap = this.getRowGap();
      this.setDelay(this.goldenCard, immediate ? 0 : 40);
      this.setDelay(this.goldenCard.querySelector('[data-gsj-card-head]'), immediate ? 0 : 120);
      this.goldenCard.querySelectorAll('[data-gsj-row]').forEach((row, index) => {
        this.setDelay(row, immediate ? 0 : 250 + (index * (gap + 15)));
      });

      this.goldenCard.classList.add('is-visible');
      if (this.footer) {
        this.setDelay(this.footer, immediate ? 0 : 360 + (this.goldenCard.querySelectorAll('[data-gsj-row]').length * (gap + 15)));
        this.footer.classList.add('is-visible');
      }

      const completeDelay = immediate
        ? 0
        : 650 + (this.goldenCard.querySelectorAll('[data-gsj-row]').length * (gap + 15));
      const timer = window.setTimeout(() => {
        if (!this.destroyed) this.goldenCard?.classList.add('is-complete');
      }, completeDelay);
      this.timers.push(timer);
    }

    renderHorizontal() {
      this.horizontalFrame = 0;
      if (!this.horizontalEnabled || !this.horizontalTrack) return;

      const progress = this.getHorizontalSectionProgress();
      const isSmallMobile = this.mobileVideoQuery.matches;
      const isTablet = this.mobileQuery.matches && !isSmallMobile;
      const transitionStart = isSmallMobile ? 0.30 : (isTablet ? 0.32 : 0.34);
      const transitionEnd = isSmallMobile ? 0.66 : (isTablet ? 0.68 : 0.70);
      const goldenRevealPoint = transitionEnd - 0.04;
      const local = Math.min(1, Math.max(0, (progress - transitionStart) / (transitionEnd - transitionStart)));
      const eased = this.easeHorizontal(local);
      const x = -(this.horizontalViewportWidth || 0) * eased;

      if (this.horizontalLastX === null || Math.abs(x - this.horizontalLastX) > 0.1) {
        this.horizontalTrack.style.transform = `translate3d(${x}px, 0, 0)`;
        this.horizontalLastX = x;
      }

      this.root.classList.toggle('is-horizontal-transitioning', local > 0 && local < 1);
      this.root.classList.toggle('is-golden-stage', progress >= goldenRevealPoint);

      if (progress >= goldenRevealPoint) this.revealGoldenStage(false);

      if (progress < transitionStart - 0.04 && this.goldenStagePlayed) {
        this.goldenStagePlayed = false;
        this.goldenCard?.classList.remove('is-visible', 'is-complete');
        if (this.footer) this.footer.classList.remove('is-visible');
      }
    }

    getRowGap() {
      const speed = this.root.dataset.staggerSpeed;
      if (speed === 'brisk') return 75;
      if (speed === 'deliberate') return 105;
      return 90;
    }

    setDelay(element, delay) {
      if (element) element.style.setProperty('--gsj-delay', `${Math.max(0, delay)}ms`);
    }

    setTimings() {
      const headerItems = Array.from(this.root.querySelectorAll('[data-gsj-header-item]'));
      headerItems.forEach((item, index) => this.setDelay(item, index * 70));

      const gap = this.getRowGap();
      if (this.horizontalEnabled) {
        this.competitorCards.forEach((card, index) => {
          const cardDelay = index === 0 ? 140 : 250;
          this.setDelay(card, cardDelay);
          this.setDelay(card.querySelector('[data-gsj-card-head]'), cardDelay + 90);
          card.querySelectorAll('[data-gsj-row]').forEach((row, rowIndex) => {
            this.setDelay(row, cardDelay + 230 + (rowIndex * gap));
          });
        });
        if (this.goldenCard) {
          this.setDelay(this.goldenCard, 0);
          this.setDelay(this.goldenCard.querySelector('[data-gsj-card-head]'), 0);
          this.goldenCard.querySelectorAll('[data-gsj-row]').forEach((row) => this.setDelay(row, 0));
        }
        this.setDelay(this.footer, 0);
        return;
      }

      if (this.mobileQuery.matches) {
        this.cards.forEach((card) => {
          this.setDelay(card, 0);
          this.setDelay(card.querySelector('[data-gsj-card-head]'), 105);
          card.querySelectorAll('[data-gsj-row]').forEach((row, index) => {
            const rowGap = card.dataset.golden === 'true' ? gap + 25 : gap;
            this.setDelay(row, 255 + (index * rowGap));
          });
        });
        this.setDelay(this.footer, 0);
        return;
      }

      let goldenLastRowDelay = 0;
      this.cards.forEach((card, index) => {
        const rows = Array.from(card.querySelectorAll('[data-gsj-row]'));
        const golden = card.dataset.golden === 'true';
        const cardDelay = golden ? 920 : (index === 0 ? 140 : 260);
        const rowStart = golden ? 1150 : (index === 0 ? 330 : 430);
        const rowGap = golden ? gap + 15 : gap;
        this.setDelay(card, cardDelay);
        this.setDelay(card.querySelector('[data-gsj-card-head]'), cardDelay + 100);
        rows.forEach((row, rowIndex) => this.setDelay(row, rowStart + (rowIndex * rowGap)));
        if (golden && rows.length) goldenLastRowDelay = rowStart + ((rows.length - 1) * rowGap);
      });
      this.setDelay(this.footer, goldenLastRowDelay + 145);
      this.desktopSequenceEnd = goldenLastRowDelay + 455;
    }

    createRevealObservers() {
      const threshold = this.mobileQuery.matches ? 0.04 : 0.22;
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            if (this.horizontalEnabled) {
              this.revealHorizontalStart();
              this.requestHorizontalRender();
            } else if (this.mobileQuery.matches) {
              this.root.classList.add('is-header-visible');
              this.played = true;
            } else {
              this.revealDesktop();
            }
          } else if (!entry.isIntersecting && this.replay) {
            const farAway = entry.boundingClientRect.bottom < -120 || entry.boundingClientRect.top > (document.documentElement.clientHeight || window.innerHeight) + 120;
            if (farAway) this.queueReset();
          }
        });
      }, { threshold: [0, threshold] });
      sectionObserver.observe(this.root);
      this.observers.push(sectionObserver);

      if (this.mobileQuery.matches && !this.horizontalEnabled) {
        const cardObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (entry.target.matches('[data-gsj-card]')) {
              this.revealCard(entry.target);
            } else {
              entry.target.classList.add('is-visible');
            }
            if (!this.replay) observer.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

        this.cards.forEach((card) => cardObserver.observe(card));
        if (this.footer) cardObserver.observe(this.footer);
        this.observers.push(cardObserver);
      }
    }

    revealDesktop(force = false) {
      if (this.played && !this.replay && !force) return;
      this.clearTimers();
      this.root.classList.add('is-header-visible');
      this.cards.forEach((card) => card.classList.add('is-visible'));
      if (this.footer) this.footer.classList.add('is-visible');
      this.played = true;

      const goldenCard = this.cards.find((card) => card.dataset.golden === 'true');
      if (goldenCard) {
        const timer = window.setTimeout(() => {
          if (!this.destroyed) goldenCard.classList.add('is-complete');
        }, this.desktopSequenceEnd || 3600);
        this.timers.push(timer);
      }
    }

    revealCard(card, immediate = false) {
      if (!card) return;
      card.classList.add('is-visible');
      if (card.dataset.golden !== 'true') return;

      const rows = card.querySelectorAll('[data-gsj-row]').length;
      const delay = immediate ? 0 : 255 + (rows * (this.getRowGap() + 25)) + 620;
      const timer = window.setTimeout(() => {
        if (!this.destroyed) card.classList.add('is-complete');
      }, delay);
      this.timers.push(timer);
    }

    revealAll(immediate = false) {
      this.root.classList.add('is-header-visible');
      this.cards.forEach((card) => {
        if (immediate) {
          this.setDelay(card, 0);
          this.setDelay(card.querySelector('[data-gsj-card-head]'), 0);
          card.querySelectorAll('[data-gsj-row]').forEach((row) => this.setDelay(row, 0));
        }
        card.classList.add('is-visible');
      });
      if (this.footer) {
        if (immediate) this.setDelay(this.footer, 0);
        this.footer.classList.add('is-visible');
      }
      this.played = true;
    }

    queueReset() {
      if (this.resetTimer) window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => this.reset(), 180);
      this.timers.push(this.resetTimer);
    }

    reset() {
      this.clearTimers();
      this.root.classList.remove('is-header-visible');
      this.cards.forEach((card) => card.classList.remove('is-visible', 'is-complete'));
      if (this.footer) this.footer.classList.remove('is-visible');
      this.played = false;
      this.setTimings();
    }

    preview() {
      this.loadAndPlayVideo(true);
      if (!this.animate || this.reducedMotionQuery.matches) {
        this.revealAll(true);
        return;
      }
      this.reset();
      if (this.horizontalEnabled) {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          this.revealHorizontalStart(true);
          this.requestHorizontalRender();
        }));
      } else {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.revealDesktop(true)));
      }
    }

    selectBlock(block) {
      if (!block) return;

      if (this.horizontalEnabled) {
        const targetProgress = block.dataset.golden === 'true' ? 0.82 : 0.18;
        const rootTop = (window.scrollY || document.documentElement.scrollTop || 0) + this.root.getBoundingClientRect().top;
        const destination = rootTop + (this.horizontalScrollDistance * targetProgress);
        window.scrollTo({
          top: destination,
          behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth'
        });
        if (block.dataset.golden === 'true') this.revealGoldenStage(true);
      }

      block.classList.add('is-editor-selected');
      this.setDelay(block, 0);
      this.setDelay(block.querySelector('[data-gsj-card-head]'), 0);
      block.querySelectorAll('[data-gsj-row]').forEach((row) => this.setDelay(row, 0));
      this.revealCard(block, true);

      const bounds = block.getBoundingClientRect();
      if (bounds.top < 0 || bounds.bottom > (document.documentElement.clientHeight || window.innerHeight)) {
        block.scrollIntoView({ behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
      }
    }

    deselectBlock(block) {
      if (block) block.classList.remove('is-editor-selected');
      this.setTimings();
    }

    disconnectRevealObservers() {
      this.observers.forEach((observer) => observer.disconnect());
      this.observers = [];
    }

    onLayoutChange() {
      this.disconnectRevealObservers();
      this.clearTimers();
      if (this.reducedMotionQuery.matches) {
        this.teardownHeroOverlap();
      } else {
        this.setupHeroOverlap();
      }
      this.syncHorizontalMode();
      this.setTimings();

      if (!this.animate || this.reducedMotionQuery.matches) {
        this.revealAll(true);
      } else {
        this.reset();
        this.createRevealObservers();
      }

      if (this.reducedMotionQuery.matches && this.video) {
        this.video.pause();
        this.video.classList.remove('is-ready');
        this.root.classList.remove('gsj-comparison--video-ready');
      }
    }

    parseSources(element) {
      const value = element?.textContent?.trim();
      if (!value || value === 'null') return [];
      try {
        const sources = JSON.parse(value);
        return Array.isArray(sources) ? sources : [];
      } catch (error) {
        return [];
      }
    }

    chooseVideoSource() {
      if (!this.video) return '';
      const desktopSources = this.parseSources(this.desktopVideoSources);
      const mobileSources = this.parseSources(this.mobileVideoSources);
      const useMobile = this.mobileVideoQuery.matches;
      const available = useMobile && mobileSources.length ? mobileSources : desktopSources;
      const mp4Sources = available.filter((source) => {
        const format = String(source.format || '').toLowerCase();
        const mime = String(source.mime_type || source.mimeType || '').toLowerCase();
        return format === 'mp4' || mime.includes('mp4') || /\.mp4(?:\?|$)/i.test(source.url || '');
      });
      const candidates = (mp4Sources.length ? mp4Sources : available)
        .filter((source) => source && source.url)
        .sort((a, b) => (Number(a.width) || 0) - (Number(b.width) || 0));
      if (!candidates.length) {
        return this.video.currentSrc || this.video.querySelector('source')?.src || '';
      }

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const targetWidth = Math.min(Math.ceil(window.innerWidth * dpr), useMobile ? 1080 : 1920);
      return (candidates.find((source) => Number(source.width) >= targetWidth) || candidates[candidates.length - 1]).url;
    }

    initVideo() {
      if (!this.video || !('IntersectionObserver' in window)) return;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.loop = true;
      this.video.controls = false;
      this.videoSource = this.video.currentSrc || this.video.querySelector('source')?.src || '';
      this.video.addEventListener('playing', this.onVideoPlaying);
      this.video.addEventListener('pause', this.onVideoPause);
      this.video.addEventListener('loadeddata', this.onVideoCanPlay);
      this.video.addEventListener('canplay', this.onVideoCanPlay);
      this.video.addEventListener('error', this.onVideoError);

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.videoNear = entry.isIntersecting;
          this.videoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
          if (entry.isIntersecting) {
            this.ensureVideoSource();
            if (this.videoVisible) this.attemptVideoPlayback();
          } else if (this.pauseVideoOffscreen) {
            this.video.pause();
          }
        });
      }, { rootMargin: '650px 0px', threshold: [0, 0.1, 0.25] });
      this.videoObserver.observe(this.root);
      this.mobileVideoQuery.addEventListener('change', this.onVideoBreakpointChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    onVideoPlaying() {
      this.playPending = false;
      this.forcePlaybackOnReady = false;
      this.video?.classList.add('is-ready');
      this.root.classList.add('gsj-comparison--video-ready');
      this.removePlaybackRetry();
    }

    onVideoPause() {
      this.playPending = false;
    }

    onVideoCanPlay() {
      this.videoCanPlay = true;
      if (this.videoVisible || this.forcePlaybackOnReady) this.attemptVideoPlayback(this.forcePlaybackOnReady);
    }

    onVideoError() {
      this.playPending = false;
      this.videoCanPlay = false;
      this.video?.classList.remove('is-ready');
      this.root.classList.remove('gsj-comparison--video-ready');
    }

    ensureVideoSource() {
      if (!this.video || this.reducedMotionQuery.matches) return false;
      const source = this.chooseVideoSource();
      if (!source) return false;
      if (this.videoSource !== source) {
        this.video.pause();
        this.videoCanPlay = false;
        this.playPending = false;
        this.video.classList.remove('is-ready');
        this.root.classList.remove('gsj-comparison--video-ready');
        this.videoSource = source;
        this.video.src = source;
        this.video.load();
      }
      return true;
    }

    attemptVideoPlayback(force = false) {
      if (!this.video || this.reducedMotionQuery.matches || document.hidden || this.playPending) return;
      if (!force && !this.videoVisible) return;
      if (!this.ensureVideoSource() || (!this.videoCanPlay && this.video.readyState < 2)) return;
      this.playPending = true;
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.playPending = false;
          this.video.classList.remove('is-ready');
          this.root.classList.remove('gsj-comparison--video-ready');
          this.addPlaybackRetry();
        });
      }
    }

    loadAndPlayVideo(force = false) {
      if (!this.ensureVideoSource()) return;
      this.forcePlaybackOnReady = force;
      if (this.videoCanPlay || this.video.readyState >= 2) this.attemptVideoPlayback(force);
    }

    addPlaybackRetry() {
      if (this.retryListening) return;
      this.retryListening = true;
      document.addEventListener('pointerdown', this.onUserPlaybackRetry, { once: true, passive: true });
      document.addEventListener('touchstart', this.onUserPlaybackRetry, { once: true, passive: true });
    }

    removePlaybackRetry() {
      this.retryListening = false;
      document.removeEventListener('pointerdown', this.onUserPlaybackRetry);
      document.removeEventListener('touchstart', this.onUserPlaybackRetry);
    }

    onUserPlaybackRetry() {
      this.removePlaybackRetry();
      if (this.videoVisible) this.attemptVideoPlayback(true);
    }

    onVideoBreakpointChange() {
      if (!this.video) return;
      window.clearTimeout(this.videoBreakpointTimer);
      this.videoBreakpointTimer = window.setTimeout(() => {
        const nextSource = this.chooseVideoSource();
        if (nextSource && nextSource !== this.videoSource) {
          this.videoSource = '';
          this.ensureVideoSource();
        }
        if (this.videoVisible) this.attemptVideoPlayback();
      }, 140);
    }

    onVisibilityChange() {
      if (!this.video) return;
      if (document.hidden) {
        this.video.pause();
      } else if (this.videoVisible) {
        this.attemptVideoPlayback();
      }
    }

    clearTimers() {
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers = [];
      this.resetTimer = null;
    }

    destroy() {
      this.destroyed = true;
      this.teardownHorizontal();
      this.teardownHeroOverlap();
      this.disconnectRevealObservers();
      this.clearTimers();
      this.mobileQuery.removeEventListener('change', this.onLayoutChange);
      this.reducedMotionQuery.removeEventListener('change', this.onLayoutChange);
      this.mobileVideoQuery.removeEventListener('change', this.onVideoBreakpointChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.clearTimeout(this.videoBreakpointTimer);
      this.removePlaybackRetry();
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.video) {
        this.video.removeEventListener('playing', this.onVideoPlaying);
        this.video.removeEventListener('pause', this.onVideoPause);
        this.video.removeEventListener('loadeddata', this.onVideoCanPlay);
        this.video.removeEventListener('canplay', this.onVideoCanPlay);
        this.video.removeEventListener('error', this.onVideoError);
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
      this.root.classList.remove('gsj-comparison--js-ready');
    }
  }

  function initialize(root) {
    if (!root || root.dataset.gsjInitialized === 'true') return;
    root.dataset.gsjInitialized = 'true';
    const instance = new GSJComparisonStaggered(root);
    instances.set(root.dataset.sectionId, instance);
  }

  function initializeWithin(container = document) {
    if (container.matches && container.matches('[data-gsj-comparison]')) initialize(container);
    container.querySelectorAll?.('[data-gsj-comparison]').forEach(initialize);
  }

  initializeWithin(document);

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-gsj-comparison]')
      ? event.target
      : event.target.querySelector?.('[data-gsj-comparison]');
    if (!root) return;
    const instance = instances.get(root.dataset.sectionId);
    if (instance) instance.destroy();
    instances.delete(root.dataset.sectionId);
    delete root.dataset.gsjInitialized;
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.matches?.('[data-gsj-comparison]')
      ? event.target
      : event.target.querySelector?.('[data-gsj-comparison]');
    if (root) instances.get(root.dataset.sectionId)?.preview();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const root = event.target.matches?.('[data-gsj-comparison]')
      ? event.target
      : event.target.querySelector?.('[data-gsj-comparison]');
    if (root) root.querySelectorAll('.is-editor-selected').forEach((card) => card.classList.remove('is-editor-selected'));
  });

  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.matches?.('[data-gsj-card]') ? event.target : event.target.closest?.('[data-gsj-card]');
    const root = card?.closest('[data-gsj-comparison]');
    if (root && card) instances.get(root.dataset.sectionId)?.selectBlock(card);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const card = event.target.matches?.('[data-gsj-card]') ? event.target : event.target.closest?.('[data-gsj-card]');
    const root = card?.closest('[data-gsj-comparison]');
    if (root && card) instances.get(root.dataset.sectionId)?.deselectBlock(card);
  });
})();
